import { toASCII, toUnicode } from 'punycode';
import { isEmailValid } from '../components/utils';

export class IDNDomainParser {
  private domain: string;
  private static readonly PUNYCODE_PREFIX = 'xn--';
  // eslint-disable-next-line no-control-regex
  private static readonly ASCII_RANGE_REGEX = /^[\x00-\x7F]*$/;
  private static readonly punycodeCache = new Map<string, string>();
  private static readonly utf8Cache = new Map<string, string>();

  constructor(private readonly value: string) {
    if (!value || typeof value !== 'string' || !value.trim()) {
      throw new Error('Invalid domain name');
    }

    this.domain = value.trim();
  }

  public static hasNonAsciiChars(text: string): boolean {
    // Check if string contains any characters outside the ASCII range (0x00-0x7F)
    return !IDNDomainParser.ASCII_RANGE_REGEX.test(text);
  }

  public static isPunycodeLabel(label: string): boolean {
    // Check if the domain label is in Punycode format (starts with 'xn--')
    return label.toLowerCase().startsWith(IDNDomainParser.PUNYCODE_PREFIX);
  }

  toPunycode(): string {
    // Check cache first
    const cached = IDNDomainParser.punycodeCache.get(this.domain);
    if (cached) {
      return cached;
    }

    const result = this.domain
      .split('.')
      .map(label => {
        try {
          // Only convert if the label contains non-ASCII characters
          return IDNDomainParser.hasNonAsciiChars(label) ? toASCII(label) : label;
        } catch (error) {
          throw new Error(
            `Failed to convert domain part "${label}" to Punycode: ${(error as Error).message}`,
          );
        }
      })
      .join('.');

    // Cache the result
    IDNDomainParser.punycodeCache.set(this.domain, result);
    return result;
  }

  toUtf8(): string {
    // Check cache first
    const cached = IDNDomainParser.utf8Cache.get(this.domain);
    if (cached) {
      return cached;
    }

    const result = this.domain
      .split('.')
      .map(label => {
        try {
          // Only convert if it's a Punycode label
          return IDNDomainParser.isPunycodeLabel(label)
            ? toUnicode(label)
            : label;
        } catch (error) {
          throw new Error(
            `Failed to convert domain part "${label}" to UTF-8: ${(error as Error).message}`,
          );
        }
      })
      .join('.');

    // Cache the result
    IDNDomainParser.utf8Cache.set(this.domain, result);
    return result;
  }
}

class IDNEmailParser {
  private email: string;
  private domain: string;
  private localPart: string;

  constructor(private readonly emailInput: string) {
    if (
      !emailInput ||
      typeof emailInput !== 'string' ||
      !emailInput.includes('@')
    ) {
      throw new Error('Invalid email address');
    }

    this.email = emailInput.trim().toLowerCase();
    const lastAtIndex = this.email.lastIndexOf('@');
    const localPart = this.email.slice(0, lastAtIndex);
    const domain = this.email.slice(lastAtIndex + 1);
    this.domain = domain;
    this.localPart = localPart;
  }

  private getDomainConverter(): IDNDomainParser {
    return new IDNDomainParser(this.domain);
  }

  toPunycode(): string {
    return `${this.localPart}@${this.getDomainConverter().toPunycode()}`;
  }

  toUtf8(): string {
    return `${this.localPart}@${this.getDomainConverter().toUtf8()}`;
  }
}

/**
 * Examples:
 *
 * Email conversion:
 * ```typescript
 * // Convert internationalized email to Punycode
 * IDNParser.parseEmail("user@münchen.de").toPunycode();  // "user@xn--mnchen-3ya.de"
 *
 * // Convert Punycode email to UTF-8
 * IDNParser.parseEmail("user@xn--mnchen-3ya.de").toUtf8();  // "user@münchen.de"
 * ```
 *
 * EmailDomain conversion:
 * ```typescript
 * // Convert internationalized domain to Punycode
 * IDNParser.parseDomain("münchen.de").toPunycode();  // "xn--mnchen-3ya.de"
 *
 * // Convert Punycode domain to UTF-8
 * IDNParser.parseDomain("xn--mnchen-3ya.de").toUtf8();  // "münchen.de"
 * ```
 */
export class IDNParser {
  private static readonly EMAIL_REGEX = /[^@]+@[^.]+\..+/;

  /**
   * Creates a converter for an email address
   * @param email The email address to convert
   * @returns An object with methods to convert the email to Punycode or UTF-8 format
   */
  static parseEmail(email: string): IDNEmailParser {
    return new IDNEmailParser(email);
  }

  /**
   * Creates a converter for a domain
   * @param domain The domain to convert
   * @returns An object with methods to convert the domain to Punycode or UTF-8 format
   */
  static parseDomain(domain: string): IDNDomainParser {
    return new IDNDomainParser(domain);
  }

  /**
   * Parses and converts UTF-8 email or domain to Punycode format
   * Handles inputs in formats:
   * - prefix:value (prefix can be a, o, b, c - a:<accountId>)
   * - domain.com
   * - email@domain.com
   * @param searchText The text to parse and convert
   * @returns The Punycode converted string if it contains non-ASCII characters, otherwise returns the original string
   */
  static parseOrderListSearchTextToUtf8(searchText: string): string {
    if (!searchText || typeof searchText !== 'string') {
      throw new Error('Invalid input');
    }

    let prefix = '';
    let actualValue = searchText;

    // Extract the prefix and actual value if it contains a colon
    if (searchText.includes(':')) {
      [prefix, actualValue] = searchText.split(':');
    }

    if (!actualValue) {
      throw new Error('Invalid input after prefix');
    }

    if (prefix && actualValue) {
      return `${prefix.toUpperCase()}:${actualValue}`;
    }

    // Convert the value and preserve the prefix if it exists
    let converted: string;

    // Check if it's an email
    if (isEmailValid(actualValue)) {
      converted = new IDNEmailParser(actualValue).toUtf8();
    } else if (actualValue.split('.').length >= 2) {
      // Treat as domain if it has at least one dot and two parts
      converted = new IDNDomainParser(actualValue).toUtf8();
    } else {
      throw new Error('Invalid email or domain format');
    }

    return converted;
  }
}

/**
 * Converts specified domain fields in a record from Punycode to UTF-8 format
 * @param item - The record containing domain fields to convert
 * @param keys - Array of keys in the record that contain domain values (defaults to ['domainName'])
 * @returns A new record with specified domain fields converted to UTF-8
 * @example
 * ```typescript
 * const record = { domainName: 'xn--mnchen-3ya.de', altDomain: 'xn--berlin-3ya.de' };
 * parseDomainToUtf8FromRecord(record, ['domainName', 'altDomain']);
 * // { domainName: 'münchen.de', altDomain: 'berlin.de' }
 * ```
 */
export const parseDomainToUtf8FromRecord = <T extends Record<string, any>> (item: T, keys: string[] = ['domainName']) => {
  return {
    ...item,
    ...(Object.fromEntries(
      keys
        .filter(key => item[key])
        .map(key => [key, IDNParser.parseDomain(item[key]).toUtf8()])
    ) || {})
  };
};

/**
 * Converts specified domain fields in an array of records from Punycode to UTF-8 format
 * @param list - Array of records containing domain fields
 * @param keys - Array of keys in each record that contain domain values
 * @returns A new array with specified domain fields converted to UTF-8 in each record
 * @example
 * ```typescript
 * const list = [
 *   { domainName: 'xn--mnchen-3ya.de' },
 *   { domainName: 'xn--berln-3ya.de' }
 * ];
 * parseDomainToUtf8FromList(list, ['domainName']);
 * // [{ domainName: 'münchen.de' }, { domainName: 'berlin.de' }]
 * ```
 */
export const parseDomainToUtf8FromList = <T extends Record<string, any>> (list: T[], keys: string[]) => {
  try {
    return list?.map((item) => parseDomainToUtf8FromRecord(item, keys)) || [];
  } catch (error) {
    console.error('Error parsing domain list:', error);
    return [];
  }
}

/**
 * Converts specified email fields in a record from Punycode to UTF-8 format
 * @param item - The record containing email fields to convert
 * @param keys - Array of keys in the record that contain email values (defaults to ['email'])
 * @returns A new record with specified email fields converted to UTF-8
 * @example
 * ```typescript
 * const record = { email: 'user@xn--mnchen-3ya.de', altEmail: 'admin@xn--berln-3ya.de' };
 * parseEmailToUtf8FromRecord(record, ['email', 'altEmail']);
 * // { email: 'user@münchen.de', altEmail: 'admin@berlin.de' }
 * ```
 */
export const parseEmailToUtf8FromRecord = <T extends Record<string, any>> (item: T, keys: string[] = ['email']) => {
  return {
    ...item,
    ...(Object.fromEntries(
      keys
        .filter(key => item[key])
        .map(key => [key, IDNParser.parseEmail(item[key]).toUtf8()])
    ) || {})
  };
};

/**
 * Converts specified email fields in an array of records from Punycode to UTF-8 format
 * @param list - Array of records containing email fields
 * @param keys - Array of keys in each record that contain email values
 * @returns A new array with specified email fields converted to UTF-8 in each record
 * @example
 * ```typescript
 * const list = [
 *   { email: 'user@xn--mnchen-3ya.de' },
 *   { email: 'admin@xn--berln-3ya.de' }
 * ];
 * parseEmailToUtf8FromList(list, ['email']);
 * // [{ email: 'user@münchen.de' }, { email: 'admin@berlin.de' }]
 * ```
 */
export const parseEmailToUtf8FromList = <T extends Record<string, any>> (list: T[], keys: string[]) =>
  list?.map((item) => parseEmailToUtf8FromRecord(item, keys)) || [];